import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { useAuth } from '../hooks/useAuth';
import MenuManagement from '../components/MenuManagement';
import ReviewManagement from '../components/ReviewManagement';
import NewsManagement from '../components/NewsManagement';
import UserManagement from '../components/UserManagement';
// Admin login modal removido conforme novo fluxo de expiração
import adminService from '../services/adminService';
import UnitsManagement from '../components/UnitsManagement';
import DishesManagement from '../components/DishesManagement';

const AdminDashboard = () => {
  const { adminUser, logout } = useAdminAuth();
  const { currentUser, isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState('menu');
  // Modal de login removido; fluxo agora redireciona ao início
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [latestNews, setLatestNews] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const isFetchingRef = useRef(false);
  const hasFetchedRef = useRef(false);
  const isFetchingNewsRef = useRef(false);
  const hasFetchedNewsRef = useRef(false);

  const loadDashboardStats = useCallback(async () => {
    // Avoid duplicate fetches and only fetch on dashboard tab
    if (isFetchingRef.current || hasFetchedRef.current) return;
    if (activeTab !== 'dashboard') return;
    try {
      setLoading(true);
      isFetchingRef.current = true;
      const response = await adminService.getDashboardStats();
      setStats(response);
      hasFetchedRef.current = true;
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    const hasAccess = ((currentUser && isAdmin && isAdmin()) || adminUser);
    if (hasAccess) {
      if (activeTab === 'dashboard') {
        loadDashboardStats();
        // Load latest news for quick overview
        (async () => {
          if (isFetchingNewsRef.current || hasFetchedNewsRef.current) return;
          try {
            setNewsLoading(true);
            isFetchingNewsRef.current = true;
            const items = await adminService.getNews();
            const sorted = Array.isArray(items) ? items.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)) : [];
            setLatestNews(sorted.slice(0, 6));
            hasFetchedNewsRef.current = true;
          } catch (error) {
            console.error('Erro ao carregar últimas notícias:', error);
            setLatestNews([]);
          } finally {
            isFetchingNewsRef.current = false;
            setNewsLoading(false);
          }
        })();
      }
    } else {
      // Sem acesso: redirecionar para a página inicial para que o usuário faça login novamente
      try { window.location.hash = 'entrada'; } catch {}
      try { window.location.href = '/'; } catch {}
    }
  }, [currentUser, adminUser, activeTab, loadDashboardStats, isAdmin]);

  // Login modal removido; não utilizado

  const handleLogout = () => {
    if (adminUser) {
      logout(); // logout from useAdminAuth
    }
    if (currentUser && isAdmin()) {
      // For regular users with admin privileges, redirect to home
      window.location.href = '/';
    }
    setStats(null);
    hasFetchedRef.current = false;
    isFetchingRef.current = false;
    setLatestNews([]);
    hasFetchedNewsRef.current = false;
    isFetchingNewsRef.current = false;
  };

  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: 'ph-chart-pie' },
    { id: 'menu', name: 'Cardápio', icon: 'ph-fork-knife' },
    { id: 'dishes', name: 'Pratos', icon: 'ph-bowl-food' },
    { id: 'reviews', name: 'Avaliações', icon: 'ph-star' },
    { id: 'news', name: 'Notícias', icon: 'ph-newspaper' },
    { id: 'users', name: 'Usuários', icon: 'ph-users' },
    { id: 'units', name: 'Unidades', icon: 'ph-buildings' }
  ];

  const displayName = adminUser?.username
    || (currentUser?.name && !String(currentUser?.name).toLowerCase().includes('undefined') ? currentUser?.name : currentUser?.username)
    || 'Administrador';

  // Fluxo antigo de exibir AdminLogin removido

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 py-3">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <span className="ml-3 px-3 py-1 bg-amber-100 text-amber-800 text-sm rounded-full">
                Bondegusto
              </span>
            </div>
            
            <div className="flex items-center gap-3 sm:space-x-4 text-sm">
              <div className="text-gray-600">
                Olá, <span className="font-medium">{displayName}</span>
              </div>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <i className="ph ph-sign-out mr-2"></i>
                Sair
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col lg:flex-row">
          {/* Sidebar / Tabs */}
          <div className="lg:w-64 lg:mr-8 w-full mb-6 lg:mb-0">
            <nav className="bg-white rounded-lg shadow-sm p-4">
              {/* Horizontal tabs in mobile/tablet, vertical list in desktop */}
              <ul className="flex lg:flex-col gap-2 lg:space-y-2 overflow-x-auto whitespace-nowrap -mx-2 px-2">
                {tabs.map((tab) => (
                  <li key={tab.id} className="flex-shrink-0">
                    <button
                      onClick={() => setActiveTab(tab.id)}
                      className={`min-w-[140px] md:min-w-[160px] lg:min-w-0 w-full flex items-center px-3 py-2 text-left rounded-lg transition-colors ${
                        activeTab === tab.id
                          ? 'bg-amber-100 text-amber-900 font-medium'
                          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <i className={`${tab.icon} mr-3 text-lg`}></i>
                      {tab.name}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {activeTab === 'dashboard' && (
              <DashboardOverview 
                stats={stats} 
                loading={loading} 
                latestNews={latestNews} 
                newsLoading={newsLoading}
                onOpenNewsTab={() => setActiveTab('news')}
                onOpenDishesTab={() => setActiveTab('dishes')}
                onOpenReviewsTab={() => setActiveTab('reviews')}
                onOpenMenuTab={() => setActiveTab('menu')}
              />
            )}
            {activeTab === 'menu' && (
              <MenuManagement />
            )}
            {activeTab === 'dishes' && (
              <DishesManagement />
            )}
            {activeTab === 'reviews' && (
              <ReviewManagement />
            )}
            {activeTab === 'news' && (
              <NewsManagement />
            )}
            {activeTab === 'users' && (
              <UserManagement />
            )}
            {activeTab === 'units' && (
              <UnitsManagement />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardOverview = ({ stats, loading, latestNews = [], newsLoading = false, onOpenNewsTab, onOpenDishesTab, onOpenReviewsTab, onOpenMenuTab }) => {
  const statCards = [
    { title: 'Itens no Cardápio', value: stats?.menu?.total || 0, icon: 'ph-bowl-food', color: 'bg-blue-500' },
    { title: 'Avaliações', value: stats?.reviews?.total || 0, icon: 'ph-star', color: 'bg-yellow-500' },
    { title: 'Notícias publicadas', value: stats?.news?.total || 0, icon: 'ph-newspaper', color: 'bg-green-500' },
  ];

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return '';
    }
  };

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((card, index) => (
          <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className={`${card.color} p-3 rounded-lg`}>
                <i className={`${card.icon} text-white text-xl`}></i>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Ações Rápidas</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button onClick={onOpenDishesTab} className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" aria-label="Abrir aba de Pratos">
            <i className="ph ph-plus text-amber-500 text-xl mr-3"></i>
            <span className="font-medium">Adicionar Prato</span>
          </button>
          <button onClick={onOpenReviewsTab} className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" aria-label="Abrir aba de Avaliações">
            <i className="ph ph-eye text-blue-500 text-xl mr-3"></i>
            <span className="font-medium">Revisar Avaliações</span>
          </button>
          <button onClick={onOpenNewsTab} className="flex items-center p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" aria-label="Abrir aba de Notícias">
            <i className="ph ph-upload text-green-500 text-xl mr-3"></i>
            <span className="font-medium">Upload de Imagem</span>
          </button>
        </div>
      </div>

      {/* Últimas Notícias */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Últimas notícias</h3>
          <button onClick={onOpenNewsTab} className="text-sm text-amber-700 hover:text-amber-900 font-medium">
            Gerenciar notícias
          </button>
        </div>
        {newsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, idx) => (
              <div key={idx} className="border border-gray-200 rounded-lg p-4 animate-pulse">
                <div className="h-32 bg-gray-200 rounded mb-3"></div>
                <div className="h-5 bg-gray-200 rounded w-2/3 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        ) : latestNews && latestNews.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {latestNews.slice(0, 6).map(item => (
              <article
                key={item._id}
                className="border border-gray-200 rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-all"
                onClick={onOpenNewsTab}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onOpenNewsTab(); }}
                aria-label={`Abrir gerenciamento de notícias para: ${item.titulo}`}
              >
                <div className="relative">
                  {item.url ? (
                    <img src={item.url} alt={item.titulo} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-gray-100 flex items-center justify-center">
                      <i className="ph ph-newspaper text-3xl text-gray-400"></i>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h4 className="font-semibold text-gray-900 line-clamp-1">{item.titulo}</h4>
                  <div className="mt-2 flex items-center text-sm text-gray-600">
                    <i className="ph ph-tag mr-1"></i>
                    <span className="capitalize">{item.categoria || 'geral'}</span>
                    {item.createdAt && (
                      <span className="ml-auto"><i className="ph ph-calendar-blank mr-1"></i>{formatDate(item.createdAt)}</span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <p className="text-gray-600">Nenhuma notícia encontrada.</p>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;